import { AcquaintanceTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/AcquaintanceTypeCodesEnum';
import { DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { ActTypesEnum } from 'KnmErknmPackage/ErknmActsModule/enums';
import { AcquaintanceLackTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/AcquaintanceLackTypeCodesEnum';
import { IKnmPmFormRescheduling } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRescheduling';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { getErknmActEventsValidate } from 'KnmErknmPackage/MainModule/common/getErknmActEventsValidate';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { getActRemarksValidate } from 'KnmErknmPackage/MainModule/common/getActRemarksValidate';
import {
  KNO_INSPECTORS_PM_LABEL,
  makeSignerInInspectorsValidator,
} from 'KnmErknmPackage/MainModule/common/getSignerInInspectorsError';

import { IKnmPmFormAct } from '../interfaces/IKnmPmFormAct';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { IKnmErknmFormSuspensionEvent } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';

interface IGetPmActValidateProps {
  act: IKnmPmFormAct | undefined;
  startDate: string | undefined;
  isValidateRequiredViolatedMandatory: boolean;
  isViolationsStartDate: boolean;
  isInteractionCategoryOrInspectorRequired: boolean | undefined;
  rescheduling: IKnmPmFormRescheduling[] | null | undefined;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  stopDate: string | undefined;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  isUnableToRefuse: boolean | undefined;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  suspensionEvents?: IKnmErknmFormSuspensionEvent[];
}

export const getPmActValidate = ({
  act,
  startDate,
  isValidateRequiredViolatedMandatory,
  isViolationsStartDate,
  isInteractionCategoryOrInspectorRequired,
  inspectorsErknm,
  isProsecutorOrSuperProsecutor,
  rescheduling,
  stopDate,
  isUnableToRefuse,
  eventsErknm,
  suspensionEvents,
}: IGetPmActValidateProps) => {
  if (!isUnableToRefuse || !act) return undefined;

  const isAcquaintanceExtended = act.acquaintanceType?.code === AcquaintanceTypeCodesEnum.ACQ_TYPE_I;
  const isAcquaintanceUnextended = act.acquaintanceType?.code === AcquaintanceTypeCodesEnum.ACQ_TYPE_II;
  const isAcquaintanceLackExtended = act.acquaintanceLackType?.code === AcquaintanceLackTypeCodesEnum.ACQ_TYPE_I;

  let knoInspectors:
    | string
    | (string | undefined)[]
    | { fullName: string | undefined; position: string | undefined }[]
    | { knoInspector: string | undefined }[]
    | undefined = !act.knoInspectors || act.knoInspectors?.length === 0 ? 'Поле не может быть пустым' : undefined;

  if (act.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE) {
    knoInspectors = undefined;
  } else if (act.knoInspectors && act.knoInspectors?.length !== 0) {
    const errors = act.knoInspectors?.map(knoInspector => {
      return { knoInspector: makeRequired('Поле не может быть пустым')(knoInspector?.knoInspector) };
    });

    // @ts-ignore
    if (errors.filter(i => i).length) {
      knoInspectors = errors;
    } else {
      knoInspectors = undefined;
    }
  }

  let holdingDateTime: string | undefined;
  let durationDays: string | undefined;
  let durationHours: string | undefined;

  if (act.typeAct?.id === ActTypesEnum.PM_PROF_ACT) {
    if (act.paperless) {
      holdingDateTime = composeValidators(
        makeRequired(REQUIRED_ERROR),
        makeNoSoonerThenValidator(
          'Дата и время начала проведения не должно быть раньше даты начала проведения ПМ',
          () => startDate,
        ),
        makeNoLaterThenValidator(
          'Дата и время начала проведения не должно быть позже даты окончания проведения ПМ',
          () => stopDate,
        ),
      )(act.holdingDateTime ? act.holdingDateTime.split(' ')[0] : act.holdingDateTime);
      durationDays = composeValidators(
        makeRequired(REQUIRED_ERROR),
        makeIsNormalInteger('Введены недопустимые символы'),
      )(act.durationDays);
      durationHours = composeValidators(
        makeRequired(REQUIRED_ERROR),
        makeIsNormalInteger('Введены недопустимые символы'),
      )(act.durationHours);
    } else {
      if (act.holdingDateTime) {
        holdingDateTime = composeValidators(
          makeNoSoonerThenValidator(
            'Дата и время начала проведения не должно быть раньше даты начала проведения ПМ',
            () => startDate,
            DATE_FORMAT_LONG_MINUTE_FNS,
          ),
          makeNoLaterThenValidator(
            'Дата и время начала проведения не должно быть позже даты окончания проведения ПМ',
            () => stopDate,
          ),
        )(act.holdingDateTime);
      }
      durationDays = makeIsNormalInteger('Введены недопустимые символы')(act.durationDays);
      durationHours = makeIsNormalInteger('Введены недопустимые символы')(act.durationHours);
    }
  }

  return {
    typeAct: makeRequired(REQUIRED_ERROR)(act.typeAct),
    canceledReason:
      act.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE
        ? makeRequired(REQUIRED_ERROR)(act.canceledReason)
        : undefined,
    canceledReasonDescription:
      act.paperless && act.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE
        ? makeRequired(REQUIRED_ERROR)(act.canceledReasonDescription)
        : undefined,
    canceledReasonDescriptionReactQuillEditor:
      act.paperless && act.typeAct?.id === ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE
        ? makeRequiredQuillEditorDelta(REQUIRED_ERROR)(act.canceledReasonDescriptionReactQuillEditor)
        : undefined,
    creationActDateTime:
      act.creationActDateTime && act.typeAct?.id !== ActTypesEnum.PM_PROF_ACT_IMPOSSIBLE
        ? makeNoSoonerThenValidator(
            'Дата и время составления акта не должны быть раньше даты начала проведения ПМ',
            () => startDate,
            DATE_FORMAT_LONG_MINUTE_FNS,
          )(act.creationActDateTime)
        : undefined,
    holdingDateTime,
    durationDays,
    durationHours,
    knoInspectors,
    fioAndPositionSinger:
      (act.paperless || act.typeAct?.id === ActTypesEnum.PM_PROF_ACT) && !act?.systemSignatureUsed
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeSignerInInspectorsValidator(act.knoInspectors, KNO_INSPECTORS_PM_LABEL),
          )(act.fioAndPositionSinger?.value.fullName)
        : undefined,
    acquaintanceType: undefined,
    fioReader: isAcquaintanceExtended ? makeRequired('Поле не может быть пустым')(act.fioReader) : undefined,
    titleReader: isAcquaintanceExtended ? makeRequired('Поле не может быть пустым')(act.titleReader) : undefined,
    fioSigner: undefined,
    titleSigner: undefined,
    refuserFio: isAcquaintanceLackExtended ? makeRequired('Поле не может быть пустым')(act.refuserFio) : undefined,
    refuserTitle: isAcquaintanceLackExtended ? makeRequired('Поле не может быть пустым')(act.refuserTitle) : undefined,
    acquaintanceLackType: isAcquaintanceUnextended
      ? makeRequired('Поле не может быть пустым')(act.acquaintanceLackType)
      : undefined,
    isViolatedMandatoryRequirements:
      act.typeAct?.id === ActTypesEnum.PM_PROF_ACT
        ? makeRequired('Поле не может быть пустым')(act.isViolatedMandatoryRequirements)
        : undefined,
    violatedMandatoryRequirements:
      act.isViolatedMandatoryRequirements?.value === true
        ? composeValidators(
            makeRequiredArray('Поле не может быть пустым'),
            getViolatedMandatoryRequirementsValidator({
              isSigned: act.paperless ? act.signed : undefined,
              isValidateRequiredViolatedMandatory,
              isDecisionMode: false,
              isProdReady: isViolationsStartDate,
            }),
          )(act.violatedMandatoryRequirements)
        : undefined,
    events:
      act.typeAct?.id === ActTypesEnum.PM_PROF_ACT
        ? getErknmActEventsValidate({
            events: act.events,
            holdingDateTime: act.holdingDateTime,
            isInteractionCategoryOrInspectorRequired,
            isPaperless: true,
            inspectorsErknm,
            isKnmStatusApproving: undefined,
            isKnmStatusTypeCompleted: true,
            isProsecutorOrSuperProsecutor,
            isShow22: true,
            isValidateActionStartDateInKnmAct: true,
            rescheduling,
            startDate,
            stopDate,
            isPm: true,
            eventsMain: eventsErknm,
            suspensionEvents,
          })
        : undefined,
    isViolationResolvedNote: act.isViolationResolved?.value
      ? makeRequired(REQUIRED_ERROR)(act.isViolationResolvedNote)
      : undefined,
    involvedExperts:
      act.paperless && act.involvedExperts?.length
        ? act.involvedExperts.map(expert => ({
            expert: makeRequired(REQUIRED_ERROR)(expert.expert),
          }))
        : undefined,
    involvedWitnesses:
      act.paperless && act.involvedWitnesses?.length
        ? act.involvedWitnesses.map(witnesses => ({
            fullName: makeRequired(REQUIRED_ERROR)(witnesses.fullName),
          }))
        : undefined,
    familiarizationNotifications: act.familiarizationNotifications?.map(fn => ({
      knmNoticeType: makeRequired(REQUIRED_ERROR)(fn?.knmNoticeType),
      noticeDate: makeRequired(REQUIRED_ERROR)(fn?.noticeDate),
    })),
    remarks: getActRemarksValidate(act?.remarks),
  };
};
